"use client";

import { format } from "date-fns";
import { useEffect, useState } from "react";

interface PublicNotification {
  id: number;
  message: string;
  updated_at?: string | null;
}

interface PublicNotificationsResponse {
  data?: PublicNotification[];
}

interface PublicNotificationBannerProps {
  publicNotificationsUrl?: string;
  placement?: "public" | "portal";
}

function formatNotificationUpdatedAt(updatedAt?: string | null) {
  if (!updatedAt) {
    return null;
  }

  const date = new Date(updatedAt);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return format(date, "dd.MM.yyyy HH:mm");
}

export default function PublicNotificationBanner({
  publicNotificationsUrl = "/api/public/notifications",
  placement = "public",
}: PublicNotificationBannerProps) {
  const [notifications, setNotifications] = useState<PublicNotification[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    fetch(publicNotificationsUrl, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: PublicNotificationsResponse | null) => {
        const items = Array.isArray(payload?.data) ? payload.data : [];
        setNotifications(
          items.filter(
            (item) => typeof item.message === "string" && item.message.trim(),
          ),
        );
      })
      .catch((error) => {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("[PublicNotificationBanner]", error);
        }
      });

    return () => controller.abort();
  }, [publicNotificationsUrl]);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <aside
      aria-label="Website notifications"
      aria-live="polite"
      className={
        placement === "portal"
          ? "border-primary/20 bg-primary text-primary-foreground relative z-40 w-full border-b shadow-sm"
          : "border-primary/20 bg-primary text-primary-foreground relative z-40 mt-16 w-full border-b shadow-sm lg:mt-[72px]"
      }
      data-testid="public-notification-banner"
      role="status"
    >
      <div className="mx-auto flex max-h-32 w-full max-w-screen-xl flex-col gap-3 overflow-y-auto px-4 py-[11px] text-center text-sm leading-snug font-semibold sm:px-6 lg:px-8">
        {notifications.map((notification) => {
          const updatedAtLabel = formatNotificationUpdatedAt(
            notification.updated_at,
          );

          return (
            <div className="space-y-1" key={notification.id}>
              <p className="break-words whitespace-pre-line">
                {notification.message}
              </p>
              {updatedAtLabel ? (
                <time
                  className="block text-xs font-medium opacity-90"
                  dateTime={notification.updated_at ?? undefined}
                >
                  Aktualisiert am {updatedAtLabel}
                </time>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
